import { useState, useEffect, useCallback, RefObject, MouseEvent } from 'react';
import { Direction } from '../atoms';

type Props<T> = {
  containerRef: RefObject<T>;
  orientation?: 'horizontal' | 'vertical';
  scrollPercentage?: number;
};

const DEFAULT_SCROLL_PERCENTAGE = 80;

export function useScrollControls<T extends HTMLElement>(
  { containerRef, orientation = 'horizontal', scrollPercentage = DEFAULT_SCROLL_PERCENTAGE }: Props<T>,
  deps: readonly any[] = []
) {
  const [isScrollable, setIsScrollable] = useState(false);
  const [isLeftScrollDisabled, setIsLeftScrollDisabled] = useState(true);
  const [isRightScrollDisabled, setIsRightScrollDisabled] = useState(true);

  const checkScrollability = useCallback(() => {
    const container = containerRef?.current;
    if (!container) return;

    const hasOverflow =
      orientation === 'horizontal'
        ? container.scrollWidth > container.clientWidth
        : container.scrollHeight > container.clientHeight;
    setIsScrollable(hasOverflow);

    if (hasOverflow) {
      setIsLeftScrollDisabled(container.scrollLeft === 0);
      // Check if scrolled to the very end (with a 1px tolerance for sub-pixel rendering)
      const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
      setIsRightScrollDisabled(isAtEnd);
    } else {
      // If not scrollable, both buttons are disabled
      setIsLeftScrollDisabled(true);
      setIsRightScrollDisabled(true);
    }
  }, [containerRef, orientation]);

  const resetScroll = useCallback(() => {
    const container = containerRef?.current;
    if (container) {
      container.scrollLeft = 0;
      container.scrollTop = 0;
    }
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(checkScrollability);
    resizeObserver.observe(container);
    container.addEventListener('scroll', checkScrollability);
    checkScrollability();

    return () => {
      resizeObserver.disconnect();
      container.removeEventListener('scroll', checkScrollability);
    };
  }, [containerRef, checkScrollability, ...deps]);

  const scrollLeft = useCallback(
    (_direction: Direction, event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      const container = containerRef?.current;
      if (container) {
        const scrollAmount = container.clientWidth * (scrollPercentage / 100);
        container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      }
    },
    [containerRef, scrollPercentage]
  );

  const scrollRight = useCallback(
    (_direction: Direction, event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      const container = containerRef?.current;
      if (container) {
        const scrollAmount = container.clientWidth * (scrollPercentage / 100);
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    },
    [containerRef, scrollPercentage]
  );

  return {
    isLeftScrollDisabled,
    isRightScrollDisabled,
    isScrollable,
    resetScroll,
    scrollLeft,
    scrollRight,
  };
}
